import React, { useState } from "react";
import { Trash2, X } from "lucide-react";
import { ActionNode } from "../../../views/convoauto-pages/bot-studio/types";
import { TemplateModule } from "../../../views/marketing-pages/templates";
import { DeleteModal } from "./action/delete-action";
import { ActionNodeCard } from "./action";

// A few action types render with a friendlier label than their raw node.type
// string (RCS types already read fine as-is, kept here for clarity/history).
const TYPE_LABEL: Record<string, string> = {
  "Send RCS Template": "Send RCS Template",
  "Send Menu": "Send Menu",
  "Send Carousel": "Send Carousel",
  "Send Text": "Send Text",
  "Wait for Reply": "Wait for Reply",
  "Set Variable": "Set Variable",
  "Escalate to Human": "Escalate to Human",
  "Confirm Order": "Confirm Order",
  "Branch": "Branch",
  "Send Email": "Send Email",
  "Call API": "Call API",
};

type MediaUploadRequest = (data: FormData) => Promise<any[]>;

interface RcsConfigPanelProps {
  node: ActionNode | null;
  onUpdate: (config: Record<string, any>) => void;
  onDelete: () => void;
  onClose: () => void;
  templates?: TemplateModule[];
  mediaUpload?: MediaUploadRequest;
  broadcastExcelUpload?: MediaUploadRequest;
  journeyId?: string | null;
  triggerEventType?: string;
}

// Right-side sliding config panel — every action type (RCS or WhatsApp) opens
// here on click instead of editing inline on the canvas card. Reuses
// ActionNodeCard's existing per-type dispatch (action.tsx) in `bare` mode so
// there's a single source of truth for which card renders which action.
export const RcsConfigPanel: React.FC<RcsConfigPanelProps> = ({
  node,
  onUpdate,
  onDelete,
  onClose,
  templates,
  mediaUpload,
  broadcastExcelUpload,
  journeyId,
  triggerEventType,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  if (!node) return null;

  return (
    <>
      <div className="w-[320px] shrink-0 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#7B7B7B] font-semibold dark:text-gray-500 font-semibold">Action</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">
              {TYPE_LABEL[node.type] ?? node.type}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowDeleteModal(true)}
              className="p-1.5 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-rose-600 hover:bg-rose-50 rounded"
              title="Delete node"
            >
              <Trash2 size={14} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
            >
              <X size={14} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 text-sm">
          <ActionNodeCard
            node={node}
            onRemove={() => setShowDeleteModal(true)}
            onUpdate={(_id, config) => onUpdate(config)}
            onReset={() => {}}
            templates={templates}
            mediaUpload={mediaUpload}
            broadcastExcelUpload={broadcastExcelUpload}
            journeyId={journeyId}
            triggerEventType={triggerEventType}
            bare
          />
        </div>
      </div>

      {showDeleteModal && (
        <DeleteModal
          title="Delete action?"
          description="This action will be removed from the flow and won't run when the trigger is activated."
          confirmLabel="Delete Action"
          onConfirm={() => {
            onDelete();
            setShowDeleteModal(false);
            onClose();
          }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </>
  );
};
